import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FaUserCheck, FaArrowRight, FaCheckCircle, FaUserPlus, FaUsers } from "react-icons/fa";
import styles from "../ClientManagement.module.css";
import { clientService } from "../../services/clientService";
import { clientTypeService } from "../../services/clientTypeService";
import { clientTypeTagsService } from "../../services/clientTypeTagsService";
import { useAccessToken } from "../../hooks/useAuth";
import { useAlert } from "../../context/AlertContext";
import { useOnboarding } from "../../hooks";

const OnboardClient = () => {
    const navigate = useNavigate();
    const { getBackendAccessToken } = useAccessToken();
    const { setErrorAlert, setInfoAlert } = useAlert();
    const { createOnboardingLink, loading: onboardingLoading } = useOnboarding();
    
    const [onboardingType, setOnboardingType] = useState(null); // 'new' or 'existing'
    const [currentStep, setCurrentStep] = useState(0); // Start at 0 for selection
    const totalSteps = 4; // Including the selection step

    // Form state
    const [formData, setFormData] = useState({
        clientCode: '',
        clientName: '',
        isActive: true,
        clientId: null  // Store created client ID
    });

    // UI state
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [isValidating, setIsValidating] = useState(false);
    const [similarClients, setSimilarClients] = useState([]);
    const [isCheckingSimilarity, setIsCheckingSimilarity] = useState(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [isGeneratingCode, setIsGeneratingCode] = useState(false);
    const [suggestedCode, setSuggestedCode] = useState('');
    const [hasNameChanged, setHasNameChanged] = useState(false);

    // Existing client search state
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [selectedExistingClient, setSelectedExistingClient] = useState(null);

    // Onboarding email state
    const [onboardingEmail, setOnboardingEmail] = useState('');
    const [emailError, setEmailError] = useState('');
    
    const [onboardingLinkCreated, setOnboardingLinkCreated] = useState(false);
    const [createdOnboardingLink, setCreatedOnboardingLink] = useState(null);

    const steps = [
        {
            id: 0,
            title: "Select Onboarding Type",
            description: "Choose whether to onboard a new or existing client"
        },
        {
            id: 1,
            title: "Create Initial Client Account",
            description: "Gather basic client details and contact information"
        },
        {
            id: 2,
            title: "Send Onboarding Link",
            description: "Enter email address to send the onboarding link"
        },
        {
            id: 3,
            title: "Ready to Begin Onboarding",
            description: "Review all information and start the onboarding process"
        }
    ];

    /**
     * Cleanup on component mount
     */
    useEffect(() => {
        // Cleanup function to cancel any pending similarity checks
        return () => {
            setSimilarClients([]);
            setIsCheckingSimilarity(false);
        };
    }, []);

    /**
     * Handle input changes
     */
    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        // Clear field-specific errors when user starts typing
        if (errors[field]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }

        // Check for similar names when client name changes
        if (field === 'clientName') {
            setHasNameChanged(true);
            // Clear similar clients if name is empty or too short
            if (!value || value.trim().length <= 2) {
                setSimilarClients([]);
                setIsCheckingSimilarity(false);
                setSuggestedCode('');
            } else {
                debouncedSimilarityCheck(value);
            }
        }

        // If user manually edits the code, clear the suggested code
        if (field === 'clientCode' && value !== suggestedCode) {
            setSuggestedCode('');
        }
    };

    /**
     * Generate client code from client name
     */
    const handleGenerateClientCode = useCallback(async () => {
        const clientName = formData.clientName.trim();
        
        if (!clientName || clientName.length <= 2) {
            return;
        }

        try {
            setIsGeneratingCode(true);
            const accessToken = await getBackendAccessToken();
            const generatedCode = await clientService.generateCode(accessToken, clientName);
            
            if (generatedCode) {
                setSuggestedCode(generatedCode);
                // Only auto-fill if the code field is empty or contains the previous suggestion
                if (!formData.clientCode || formData.clientCode === suggestedCode) {
                    setFormData(prev => ({
                        ...prev,
                        clientCode: generatedCode
                    }));
                }
            } else {
                setSuggestedCode('');
            }
        } catch (error) {
            console.error('Error generating client code:', error);
            setSuggestedCode('');
        } finally {
            setIsGeneratingCode(false);
        }
    }, [formData.clientName, formData.clientCode, suggestedCode, getBackendAccessToken]);

    /**
     * Handle client name changes and check for similar names
     */
    const handleClientNameChange = useCallback(async (value) => {
        // Clear existing similar clients if name is too short
        if (value.trim().length <= 2) {
            setSimilarClients([]);
            return;
        }

        try {
            setIsCheckingSimilarity(true);
            const accessToken = await getBackendAccessToken();
            const similar = await clientService.findSimilarNames(accessToken, value.trim(), 0.6);
            
            // Show all similar clients including exact matches (potential duplicates)
            setSimilarClients(similar || []);
        } catch (error) {
            console.error('Error checking for similar client names:', error);
            setSimilarClients([]);
        } finally {
            setIsCheckingSimilarity(false);
        }
    }, [getBackendAccessToken]);

    // Debounce the similarity check to avoid excessive API calls
    const debouncedSimilarityCheck = useCallback(
        (() => {
            let timeoutId;
            return (value) => {
                clearTimeout(timeoutId);
                timeoutId = setTimeout(() => handleClientNameChange(value), 500);
            };
        })(),
        [handleClientNameChange]
    );

    /**
     * Validate client code uniqueness in real-time
     */
    const handleClientCodeBlur = useCallback(async () => {
        const clientCode = formData.clientCode.trim();
        
        if (!clientCode) return;

        try {
            setIsValidating(true);
            const accessToken = await getBackendAccessToken();
            const exists = await clientService.checkCodeExists(accessToken, clientCode);
            
            if (exists) {
                setErrors(prev => ({
                    ...prev,
                    clientCode: 'Client code already exists'
                }));
            }
        } catch (error) {
            console.error('Error checking client code uniqueness:', error);
            // Don't show error to user for this validation check
        } finally {
            setIsValidating(false);
        }
    }, [formData.clientCode, getBackendAccessToken]);

    /**
     * Validate form data
     */
    const validateForm = () => {
        const newErrors = {};

        // Client name is now required
        if (!formData.clientName.trim()) {
            newErrors.clientName = 'Client name is required';
        } else if (formData.clientName.length > 50) {
            newErrors.clientName = 'Client name cannot exceed 50 characters';
        }

        // Validate client code if provided
        if (formData.clientCode.trim()) {
            if (formData.clientCode.length > 6) {
                newErrors.clientCode = 'Client code cannot exceed 6 characters';
            }
        }

        return newErrors;
    };

    const handleNext = async () => {
        // If we're on step 1 (client info for new clients) and moving forward, trigger validation and creation
        if (currentStep === 1 && onboardingType === 'new') {
            await handleCreateAndNext();
        } else if (currentStep < totalSteps - 1) {
            setCurrentStep(currentStep + 1);
        }
    };

    const handleSelectOnboardingType = (type) => {
        setOnboardingType(type);
        setCurrentStep(1);
        // Reset search state when switching types
        if (type === 'existing') {
            setSearchQuery('');
            setSearchResults([]);
            setSelectedExistingClient(null);
        }
    };

    const handleCreateAndNext = async () => {
        // Validate form
        const validationErrors = validateForm();
        setErrors(validationErrors);

        if (Object.keys(validationErrors).length > 0) {
            return;
        }

        // Check if there are similar clients and show confirmation dialog
        if (similarClients.length > 0 && !showConfirmDialog) {
            setShowConfirmDialog(true);
            return;
        }

        // Create the client and move to next step
        await createClientAndContinue();
    };

    const handlePrevious = () => {
        if (currentStep > 0) {
            if (currentStep === 1) {
                // Going back to selection resets the type
                setOnboardingType(null);
                setSearchQuery('');
                setSearchResults([]);
                setSelectedExistingClient(null);
            }
            setCurrentStep(currentStep - 1);
        }
    };

    /**
     * Search for existing clients by code or name
     */
    const handleSearchClients = useCallback(async (query) => {
        if (!query || query.trim().length < 2) {
            setSearchResults([]);
            return;
        }

        try {
            setIsSearching(true);
            const accessToken = await getBackendAccessToken();
            const searchTerm = query.trim();
            
            // Search by name first
            let results = await clientService.search(accessToken, searchTerm);
            
            // If search term looks like it could be a code (short, uppercase), also try exact code lookup
            if (searchTerm.length <= 6) {
                try {
                    const codeResult = await clientService.getByCode(accessToken, searchTerm.toUpperCase());
                    // Add code result if it's not already in the results
                    if (codeResult && !results.some(r => r.clientId === codeResult.clientId)) {
                        results = [codeResult, ...results];
                    }
                } catch (codeError) {
                    // Code not found is okay, just means no exact match
                    console.log('No exact code match found');
                }
            }
            
            // Also search for partial code matches in all clients
            const allClients = await clientService.getAll(accessToken);
            const codeMatches = allClients.filter(client => 
                client.clientCode && 
                client.clientCode.toLowerCase().includes(searchTerm.toLowerCase()) &&
                !results.some(r => r.clientId === client.clientId)
            );
            
            results = [...results, ...codeMatches];
            
            setSearchResults(results);
        } catch (error) {
            console.error('Error searching for clients:', error);
            setErrorAlert('Failed to search for clients. Please try again.');
            setSearchResults([]);
        } finally {
            setIsSearching(false);
        }
    }, [getBackendAccessToken, setErrorAlert]);

    // Debounce the client search
    const debouncedClientSearch = useCallback(
        (() => {
            let timeoutId;
            return (value) => {
                clearTimeout(timeoutId);
                timeoutId = setTimeout(() => handleSearchClients(value), 300);
            };
        })(),
        [handleSearchClients]
    );

    /**
     * Handle search input change
     */
    const handleSearchInputChange = (value) => {
        setSearchQuery(value);
        debouncedClientSearch(value);
    };

    /**
     * Handle selecting an existing client
     */
    const handleSelectExistingClient = (client) => {
        setSelectedExistingClient(client);
        setFormData(prev => ({
            ...prev,
            clientId: client.clientId,
            clientCode: client.clientCode || '',
            clientName: client.clientName || '',
            isActive: client.isActive
        }));
        setSearchQuery('');
        setSearchResults([]);
    };

    /**
     * Create the client and continue to next step
     */
    const createClientAndContinue = async () => {
        try {
            setLoading(true);
            setShowConfirmDialog(false);
            const accessToken = await getBackendAccessToken();
            
            // Prepare data for API - client name is now required
            const clientData = {
                clientName: formData.clientName.trim(),
                isActive: formData.isActive
            };
            
            // Only add client code if it was provided
            if (formData.clientCode.trim()) {
                clientData.clientCode = formData.clientCode.trim();
            }

            // Create the client
            const createdClient = await clientService.create(accessToken, clientData);
            
            // Store the created client ID for later use
            setFormData(prev => ({
                ...prev,
                clientId: createdClient.clientId
            }));
            
            setInfoAlert('Client account created successfully');
            
            // Move to next step
            setCurrentStep(2);
        } catch (error) {
            console.error('Failed to create client:', error);
            setErrorAlert('Failed to create client. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    /**
     * Handle form submission - Create onboarding link and assign tags
     */
    const handleComplete = async () => {
        try {
            setLoading(true);
            
            // Validate email
            if (!onboardingEmail || emailError) {
                setErrorAlert('Please provide a valid email address');
                return;
            }
            
            const accessToken = await getBackendAccessToken();
            
            // Create onboarding link
            try {
                const onboardingRequest = {
                    clientId: formData.clientId,  // Top-level field for backend tracking
                    clientEmail: onboardingEmail,
                    clientName: formData.clientName || 'Client',
                    prefillData: {
                        clientCode: formData.clientCode,
                        companyName: formData.clientName
                    }
                };
                
                console.log('Creating onboarding link with request:', onboardingRequest);
                const response = await createOnboardingLink(onboardingRequest);
                
                if (response && response.url) {
                    setCreatedOnboardingLink(response.url);
                    setOnboardingLinkCreated(true);
                    setInfoAlert(`Onboarding link sent to ${onboardingEmail}`);
                } else if (response && response.error === 'duplicate_onboarding_request') {
                    // Backend detected duplicate - show the backend's message
                    setErrorAlert(response.message || 'An active onboarding token already exists for this client.');
                    return;
                } else {
                    throw new Error('Failed to create onboarding link');
                }
            } catch (onboardingError) {
                console.error('Failed to create onboarding link:', onboardingError);
                // Check if error response has a message from backend
                if (onboardingError.message && onboardingError.message.includes('duplicate_onboarding_request')) {
                    setErrorAlert('An active onboarding token already exists for this client. Please wait for it to expire or cancel it before sending a new request.');
                } else {
                    setErrorAlert('Failed to create onboarding link. Please try again.');
                }
                return;
            }
        } catch (error) {
            console.error('Failed to complete onboarding:', error);
            setErrorAlert('Failed to complete onboarding. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    /**
     * Assign tags and complete onboarding
     */
    const assignTagsAndComplete = async () => {
        try {
            setLoading(true);
            const accessToken = await getBackendAccessToken();
            
            // Assign selected client type tags if any are selected
            if (selectedClientTypes.length > 0 && formData.clientId) {
                try {
                    await clientTypeTagsService.assignClientTypesToClient(
                        accessToken, 
                        formData.clientId, 
                        selectedClientTypes
                    );
                    
                    if (selectedClientTypes.length === 1) {
                        setInfoAlert('Client onboarding completed with 1 client type tag assigned');
                    } else {
                        setInfoAlert(`Client onboarding completed with ${selectedClientTypes.length} client type tags assigned`);
                    }
                } catch (tagError) {
                    console.error('Failed to assign client type tags:', tagError);
                    setErrorAlert('Failed to assign some client type tags. You can assign them manually later.');
                }
            } else {
                setInfoAlert('Client onboarding completed');
            }
            
            navigate('/clients/list');
        } catch (error) {
            console.error('Failed to complete onboarding:', error);
            setErrorAlert('Failed to complete onboarding. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    /**
     * Create the client after validation and confirmation
     */
    const createClient = async () => {
        try {
            setLoading(true);
            setShowConfirmDialog(false);
            const accessToken = await getBackendAccessToken();
            
            // Prepare data for API - client name is now required
            const clientData = {
                clientName: formData.clientName.trim(),
                isActive: formData.isActive
            };
            
            // Only add client code if it was provided
            if (formData.clientCode.trim()) {
                clientData.clientCode = formData.clientCode.trim();
            }

            // Create the client first
            const createdClient = await clientService.create(accessToken, clientData);
            
            // Assign selected client type tags if any are selected
            if (selectedClientTypes.length > 0 && createdClient.clientId) {
                try {
                    await clientTypeTagsService.assignClientTypesToClient(
                        accessToken, 
                        createdClient.clientId, 
                        selectedClientTypes
                    );
                    
                    if (selectedClientTypes.length === 1) {
                        setInfoAlert('Client onboarded successfully with 1 client type tag assigned');
                    } else {
                        setInfoAlert(`Client onboarded successfully with ${selectedClientTypes.length} client type tags assigned`);
                    }
                } catch (tagError) {
                    console.error('Failed to assign client type tags:', tagError);
                    setErrorAlert('Client onboarded successfully, but failed to assign some client type tags. You can assign them manually later.');
                }
            } else {
                setInfoAlert('Client onboarded successfully');
            }
            
            navigate('/clients/list');
        } catch (error) {
            console.error('Failed to onboard client:', error);
            setErrorAlert('Failed to onboard client. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const renderStepContent = () => {
        switch (currentStep) {
            case 0:
                return (
                    <div className={styles.formSection}>
                        <h3>Select Onboarding Type</h3>
                        <p className={styles.sectionDescription}>
                            Choose whether you want to onboard a new client or an existing client.
                        </p>
                        <div style={{ 
                            display: 'grid', 
                            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
                            gap: '2rem', 
                            marginTop: '2rem' 
                        }}>
                            <button
                                type="button"
                                onClick={() => handleSelectOnboardingType('new')}
                                style={{
                                    padding: '2rem',
                                    background: 'var(--background-color)',
                                    border: '2px solid var(--border-color)',
                                    borderRadius: '12px',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: '1rem'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.borderColor = 'var(--primary-color)';
                                    e.currentTarget.style.transform = 'translateY(-4px)';
                                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(33, 150, 243, 0.3)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.borderColor = 'var(--border-color)';
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = 'none';
                                }}
                            >
                                <FaUserPlus style={{ fontSize: '3rem', color: 'var(--primary-color)' }} />
                                <h4 style={{ margin: 0, color: 'var(--text-color)' }}>Onboard New Client</h4>
                                <p style={{ margin: 0, color: 'var(--text-color)', opacity: 0.7, textAlign: 'center' }}>
                                    Create a new client account and complete the onboarding process
                                </p>
                            </button>

                            <button
                                type="button"
                                onClick={() => handleSelectOnboardingType('existing')}
                                style={{
                                    padding: '2rem',
                                    background: 'var(--background-color)',
                                    border: '2px solid var(--border-color)',
                                    borderRadius: '12px',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: '1rem'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.borderColor = 'var(--primary-color)';
                                    e.currentTarget.style.transform = 'translateY(-4px)';
                                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(33, 150, 243, 0.3)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.borderColor = 'var(--border-color)';
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = 'none';
                                }}
                            >
                                <FaUsers style={{ fontSize: '3rem', color: 'var(--primary-color)' }} />
                                <h4 style={{ margin: 0, color: 'var(--text-color)' }}>Onboard Existing Client</h4>
                                <p style={{ margin: 0, color: 'var(--text-color)', opacity: 0.7, textAlign: 'center' }}>
                                    Select an existing client and complete additional onboarding steps
                                </p>
                            </button>
                        </div>
                    </div>
                );
            case 1:
                if (onboardingType === 'existing') {
                    return (
                        <div className={styles.formSection}>
                            <h3>Select Existing Client</h3>
                            <p className={styles.sectionDescription}>
                                Search for and select an existing client to onboard.
                            </p>
                            
                            {!selectedExistingClient ? (
                                <>
                                    <div className={styles.formGroup}>
                                        <label>Search for Client</label>
                                        <input
                                            type="text"
                                            placeholder="Search by client code or name..."
                                            value={searchQuery}
                                            onChange={(e) => handleSearchInputChange(e.target.value)}
                                            disabled={loading}
                                        />
                                        {isSearching && (
                                            <div className={styles.validatingMessage}>
                                                Searching...
                                            </div>
                                        )}
                                    </div>
                                    
                                    {/* Search Results */}
                                    {searchResults.length > 0 && (
                                        <div style={{ marginTop: '1rem' }}>
                                            <h4 style={{ margin: '0 0 1rem 0', color: 'var(--text-color)', fontSize: '0.95rem' }}>
                                                Search Results ({searchResults.length})
                                            </h4>
                                            <div style={{ 
                                                display: 'flex', 
                                                flexDirection: 'column', 
                                                gap: '0.5rem',
                                                maxHeight: '400px',
                                                overflowY: 'auto',
                                                border: '1px solid var(--border-color)',
                                                borderRadius: '6px',
                                                padding: '0.5rem'
                                            }}>
                                                {searchResults.map(client => (
                                                    <button
                                                        key={client.clientId}
                                                        type="button"
                                                        onClick={() => handleSelectExistingClient(client)}
                                                        style={{
                                                            padding: '1rem',
                                                            background: 'var(--background-color)',
                                                            border: '1px solid var(--border-color)',
                                                            borderRadius: '6px',
                                                            cursor: 'pointer',
                                                            transition: 'all 0.2s ease',
                                                            textAlign: 'left'
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            e.currentTarget.style.borderColor = 'var(--primary-color)';
                                                            e.currentTarget.style.background = 'var(--secondary-color)';
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.currentTarget.style.borderColor = 'var(--border-color)';
                                                            e.currentTarget.style.background = 'var(--background-color)';
                                                        }}
                                                    >
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                                {client.clientCode && (
                                                                    <span style={{
                                                                        padding: '0.25rem 0.5rem',
                                                                        background: 'var(--primary-color)',
                                                                        color: 'white',
                                                                        fontSize: '0.75rem',
                                                                        fontWeight: 'bold',
                                                                        borderRadius: '4px'
                                                                    }}>
                                                                        {client.clientCode}
                                                                    </span>
                                                                )}
                                                                <span style={{ fontWeight: 'bold', color: 'var(--text-color)' }}>
                                                                    {client.clientName}
                                                                </span>
                                                                <span style={{
                                                                    padding: '0.25rem 0.5rem',
                                                                    background: client.isActive ? '#E8F5E8' : '#FFEBEE',
                                                                    color: client.isActive ? '#2E7D32' : '#C62828',
                                                                    fontSize: '0.7rem',
                                                                    fontWeight: 'bold',
                                                                    borderRadius: '4px',
                                                                    textTransform: 'uppercase'
                                                                }}>
                                                                    {client.isActive ? 'Active' : 'Inactive'}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    
                                    {searchQuery.length >= 2 && searchResults.length === 0 && !isSearching && (
                                        <div className={styles.noResults}>
                                            No clients found matching "{searchQuery}"
                                        </div>
                                    )}
                                </>
                            ) : (
                                <>
                                    <div style={{
                                        padding: '1.5rem',
                                        background: 'var(--secondary-color)',
                                        border: '2px solid var(--primary-color)',
                                        borderRadius: '8px',
                                        marginTop: '1rem'
                                    }}>
                                        <h4 style={{ margin: '0 0 1rem 0', color: 'var(--text-color)' }}>
                                            Selected Client
                                        </h4>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                                            {selectedExistingClient.clientCode && (
                                                <span style={{
                                                    padding: '0.25rem 0.5rem',
                                                    background: 'var(--primary-color)',
                                                    color: 'white',
                                                    fontSize: '0.75rem',
                                                    fontWeight: 'bold',
                                                    borderRadius: '4px'
                                                }}>
                                                    {selectedExistingClient.clientCode}
                                                </span>
                                            )}
                                            <span style={{ fontWeight: 'bold', color: 'var(--text-color)', fontSize: '1.1rem' }}>
                                                {selectedExistingClient.clientName}
                                            </span>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setSelectedExistingClient(null);
                                                setFormData(prev => ({
                                                    ...prev,
                                                    clientId: null,
                                                    clientCode: '',
                                                    clientName: '',
                                                    isActive: true
                                                }));
                                            }}
                                            style={{
                                                marginTop: '1rem',
                                                padding: '0.5rem 1rem',
                                                background: 'var(--border-color)',
                                                border: '1px solid var(--border-color)',
                                                borderRadius: '4px',
                                                cursor: 'pointer',
                                                color: 'var(--text-color)'
                                            }}
                                        >
                                            Change Selection
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    );
                }
                // For new clients, show the create form
                return (
                    <div className={styles.formSection}>
                        <h3>Client Information</h3>
                        <p className={styles.sectionDescription}>
                            Enter the client name first. We'll suggest a client code for you.
                        </p>
                        
                        {/* General Error Message */}
                        {errors.general && (
                            <div className={styles.errorMessage}>
                                {errors.general}
                            </div>
                        )}
                        
                        <div className={styles.formRow}>
                            {/* CLIENT NAME FIRST */}
                            <div className={styles.formGroup}>
                                <label htmlFor="clientName">
                                    Client Name 
                                    <span className={styles.required}>*</span>
                                    <span className={styles.optional}> (Max 50 chars)</span>
                                </label>
                                <input
                                    id="clientName"
                                    type="text"
                                    placeholder="e.g., Acme Corporation"
                                    value={formData.clientName}
                                    onChange={(e) => handleInputChange('clientName', e.target.value)}
                                    onBlur={() => {
                                        // Generate code when user leaves the name field if name was changed
                                        if (hasNameChanged && formData.clientName.trim().length > 2) {
                                            handleGenerateClientCode();
                                            setHasNameChanged(false);
                                        }
                                    }}
                                    maxLength={50}
                                    disabled={loading}
                                    autoFocus
                                />
                                <div className={styles.counter}>
                                    {formData.clientName.length}/50 characters
                                </div>
                                {errors.clientName && (
                                    <div className={styles.errorMessage}>
                                        {errors.clientName}
                                    </div>
                                )}
                                {isCheckingSimilarity && (
                                    <div className={styles.validatingMessage}>
                                        Checking for similar clients...
                                    </div>
                                )}
                                {similarClients.length > 0 && (() => {
                                    const exactMatches = similarClients.filter(client => 
                                        client.clientName && 
                                        client.clientName.toLowerCase() === formData.clientName.trim().toLowerCase()
                                    );
                                    const similarMatches = similarClients.filter(client => 
                                        client.clientName && 
                                        client.clientName.toLowerCase() !== formData.clientName.trim().toLowerCase()
                                    );
                                    
                                    return (
                                        <div className={styles.warningMessage}>
                                            {exactMatches.length > 0 && (
                                                <>
                                                    <strong>🚨 Exact Match Found:</strong> A client with this exact name already exists:
                                                    <div className={styles.similarClientsList}>
                                                        <ul>
                                                            {exactMatches.map(client => (
                                                                <li key={client.clientId} style={{ color: '#d32f2f', fontWeight: 'bold' }}>
                                                                    <span className={styles.clientInfo}>
                                                                        {client.clientCode ? `${client.clientCode} - ` : ''}{client.clientName}
                                                                    </span>
                                                                    {!client.isActive && (
                                                                        <span className={styles.inactiveClient}> (Inactive)</span>
                                                                    )}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                </>
                                            )}
                                            {similarMatches.length > 0 && (
                                                <>
                                                    {exactMatches.length > 0 && <br />}
                                                    <strong>⚠️ Similar Clients Found:</strong>
                                                    <div className={styles.similarClientsList}>
                                                        <ul>
                                                            {similarMatches.map(client => (
                                                                <li key={client.clientId}>
                                                                    <span className={styles.clientInfo}>
                                                                        {client.clientCode ? `${client.clientCode} - ` : ''}{client.clientName}
                                                                    </span>
                                                                    {!client.isActive && (
                                                                        <span className={styles.inactiveClient}> (Inactive)</span>
                                                                    )}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                </>
                                            )}
                                            <div className={styles.warningNote}>
                                                {exactMatches.length > 0 
                                                    ? 'This appears to be a duplicate client. Please verify before proceeding.'
                                                    : 'Please verify this is not a duplicate before proceeding.'
                                                }
                                            </div>
                                        </div>
                                    );
                                })()}
                            </div>

                            {/* CLIENT CODE SECOND */}
                            <div className={styles.formGroup}>
                                <label htmlFor="clientCode">
                                    Client Code 
                                    <span className={styles.optional}>(Max 6 chars)</span>
                                </label>
                                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                                    <div style={{ flex: 1 }}>
                                        <input
                                            id="clientCode"
                                            type="text"
                                            placeholder="Auto-generated or enter manually"
                                            value={formData.clientCode}
                                            onChange={(e) => handleInputChange('clientCode', e.target.value.toUpperCase())}
                                            onBlur={handleClientCodeBlur}
                                            maxLength={6}
                                            disabled={loading}
                                        />
                                        <div className={styles.counter}>
                                            {formData.clientCode.length}/6 characters
                                        </div>
                                        {isGeneratingCode && (
                                            <div className={styles.validatingMessage}>
                                                Generating suggested code...
                                            </div>
                                        )}
                                        {suggestedCode && formData.clientCode === suggestedCode && (
                                            <div style={{ 
                                                marginTop: '0.5rem', 
                                                padding: '0.5rem', 
                                                background: '#E8F5E9', 
                                                color: '#2E7D32',
                                                borderRadius: '4px',
                                                fontSize: '0.85rem'
                                            }}>
                                                ✓ Suggested code applied. You can edit it if needed.
                                            </div>
                                        )}
                                    </div>
                                    {formData.clientName.trim().length > 2 && (
                                        <button
                                            type="button"
                                            onClick={handleGenerateClientCode}
                                            disabled={isGeneratingCode || loading}
                                            style={{
                                                padding: '0.5rem 1rem',
                                                background: 'var(--primary-color)',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '4px',
                                                cursor: isGeneratingCode || loading ? 'not-allowed' : 'pointer',
                                                fontSize: '0.85rem',
                                                whiteSpace: 'nowrap',
                                                marginTop: '0.15rem'
                                            }}
                                        >
                                            {isGeneratingCode ? 'Generating...' : 'Generate Code'}
                                        </button>
                                    )}
                                </div>
                                {errors.clientCode && (
                                    <div className={styles.errorMessage}>
                                        {errors.clientCode}
                                    </div>
                                )}
                                {isValidating && (
                                    <div className={styles.validatingMessage}>
                                        Checking availability...
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className={styles.formGroup}>
                            <label className={styles.checkboxLabel}>
                                <input
                                    type="checkbox"
                                    checked={formData.isActive}
                                    onChange={(e) => handleInputChange('isActive', e.target.checked)}
                                    disabled={loading}
                                />
                                <span>Client is active</span>
                            </label>
                        </div>
                    </div>
                );
            case 2:
                return (
                    <div className={styles.formSection}>
                        <h3>Send Onboarding Link</h3>
                        <p className={styles.sectionDescription}>
                            Enter the email address to send the secure onboarding link.
                        </p>
                        
                        <div className={styles.formGroup}>
                            <label htmlFor="onboardingEmail">
                                Email Address
                                <span className={styles.required}>*</span>
                            </label>
                            <input
                                id="onboardingEmail"
                                type="email"
                                placeholder="e.g., contact@client.com"
                                value={onboardingEmail}
                                onChange={(e) => {
                                    setOnboardingEmail(e.target.value);
                                    setEmailError('');
                                }}
                                onBlur={() => {
                                    // Basic email validation
                                    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                                    if (onboardingEmail && !emailPattern.test(onboardingEmail)) {
                                        setEmailError('Please enter a valid email address');
                                    } else {
                                        setEmailError('');
                                    }
                                }}
                                disabled={loading}
                            />
                            {emailError && (
                                <div className={styles.errorMessage}>
                                    {emailError}
                                </div>
                            )}
                            <div className={styles.helpText} style={{ marginTop: '0.5rem', fontSize: '0.85rem', opacity: 0.7 }}>
                                A secure onboarding link will be sent to this email address. The recipient can use it to complete their onboarding process.
                            </div>
                        </div>
                    </div>
                );
            case 3:
                return (
                    <div className={styles.formSection}>
                        {onboardingLinkCreated ? (
                            <>
                                <h3>Onboarding Package Sent!</h3>
                                <div style={{ padding: '2rem', textAlign: 'center' }}>
                                    <FaCheckCircle style={{ fontSize: '4rem', color: '#4CAF50', marginBottom: '1rem' }} />
                                    <h4 style={{ marginBottom: '1rem', color: 'var(--text-color)' }}>Onboarding Link Sent Successfully</h4>
                                    <p style={{ color: 'var(--text-color)', opacity: 0.8, marginBottom: '2rem' }}>
                                        A secure onboarding link has been sent to <strong>{onboardingEmail}</strong>.
                                        The client can use this link to complete their onboarding form.
                                    </p>
                                    
                                    <div style={{ marginTop: '2rem' }}>
                                        <button
                                            className={styles.submitButton}
                                            onClick={() => navigate('/clients/list')}
                                        >
                                            Go to Client List
                                        </button>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <>
                                <h3>Ready to Begin Onboarding</h3>
                                <p className={styles.sectionDescription}>
                                    Review all information before initiating the client onboarding process.
                                </p>
                                <div style={{ padding: '2rem', textAlign: 'center' }}>
                                    <FaCheckCircle style={{ fontSize: '3rem', color: 'var(--primary-color)', marginBottom: '1rem' }} />
                                    <h4 style={{ marginBottom: '0.5rem', color: 'var(--text-color)' }}>Ready to Start Onboarding</h4>
                                    <p style={{ color: 'var(--text-color)', opacity: 0.7 }}>
                                        Please review all information in the previous steps. Once you click "Begin Onboarding", 
                                        an onboarding link will be sent to the specified email address to start the {onboardingType === 'new' ? 'new ' : ''}client onboarding process.
                                    </p>

                                    {/* Display summary */}
                                    <div style={{ marginTop: '2rem', textAlign: 'left', maxWidth: '500px', margin: '2rem auto 0' }}>
                                        <div style={{ background: 'var(--secondary-color)', padding: '1.5rem', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                                            <h5 style={{ margin: '0 0 1rem 0', color: 'var(--text-color)' }}>Summary:</h5>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                                <div>
                                                    <strong>Onboarding Type:</strong> {onboardingType === 'new' ? 'New Client' : 'Existing Client'}
                                                </div>
                                                {formData.clientCode && (
                                                    <div>
                                                        <strong>Client Code:</strong> {formData.clientCode}
                                                    </div>
                                                )}
                                                {formData.clientName && (
                                                    <div>
                                                        <strong>Client Name:</strong> {formData.clientName}
                                                    </div>
                                                )}
                                                {onboardingType === 'new' && (
                                                    <div>
                                                        <strong>Status:</strong> {formData.isActive ? 'Active' : 'Inactive'}
                                                    </div>
                                                )}
                                                {onboardingEmail && (
                                                    <div>
                                                        <strong>Onboarding Email:</strong> {onboardingEmail}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <div className={styles.addClientSection}>
            <h2>
                <FaUserCheck style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
                Onboard Client
            </h2>

            {/* Progress Indicator - Only show after selection */}
            {currentStep > 0 && (
                <div style={{ marginBottom: '2rem' }}>
                    <div style={{ 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center',
                        marginBottom: '1rem',
                        gap: '0'
                    }}>
                        {steps.slice(1).map((step, index) => (
                            <div key={step.id} style={{ 
                                display: 'flex', 
                                alignItems: 'center',
                                position: 'relative'
                            }}>
                                <div style={{
                                    width: '40px',
                                    height: '40px',
                                    borderRadius: '50%',
                                    background: currentStep >= step.id ? 'var(--primary-color)' : 'var(--border-color)',
                                    color: currentStep >= step.id ? 'white' : 'var(--text-color)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontWeight: 'bold',
                                    zIndex: 1,
                                    flexShrink: 0
                                }}>
                                    {currentStep > step.id ? <FaCheckCircle /> : step.id}
                                </div>
                                {index < steps.length - 2 && (
                                    <div style={{
                                        width: '120px',
                                        height: '2px',
                                        background: currentStep > step.id ? 'var(--primary-color)' : 'var(--border-color)',
                                        margin: '0 0.5rem',
                                        flexShrink: 0
                                    }} />
                                )}
                            </div>
                        ))}
                    </div>
                    <div style={{
                        textAlign: 'center',
                        color: 'var(--text-color)',
                        marginTop: '1rem'
                    }}>
                        <h3 style={{ margin: '0 0 0.5rem 0' }}>{steps[currentStep].title}</h3>
                        <p style={{ margin: 0, opacity: 0.7, fontSize: '0.9rem' }}>
                            {steps[currentStep].description}
                        </p>
                    </div>
                </div>
            )}

            <div className={styles.formContainer}>
                {renderStepContent()}

                <div className={styles.formActions}>
                    <button 
                        className={styles.cancelButton}
                        onClick={() => navigate('/clients')}
                        disabled={loading}
                    >
                        Cancel
                    </button>
                    {currentStep > 0 && (
                        <button 
                            className={styles.cancelButton}
                            onClick={handlePrevious}
                            disabled={loading}
                        >
                            Previous
                        </button>
                    )}
                    {currentStep === 0 ? null : onboardingLinkCreated ? null : currentStep < totalSteps - 1 ? (
                        <button 
                            className={styles.submitButton}
                            onClick={currentStep === 1 && onboardingType === 'new' ? handleCreateAndNext : handleNext}
                            disabled={
                                loading || 
                                (currentStep === 1 && onboardingType === 'new' && (
                                    isValidating || 
                                    isCheckingSimilarity || 
                                    isGeneratingCode ||
                                    Object.keys(errors).length > 0 ||
                                    !formData.clientName.trim() // Require client name
                                )) ||
                                (currentStep === 1 && onboardingType === 'existing' && !selectedExistingClient) ||
                                (currentStep === 2 && (!onboardingEmail || emailError)) // Validate email on step 2
                            }
                        >
                            {currentStep === 1 && onboardingType === 'new' ? (
                                <>
                                    {loading ? 'Creating Client...' : 'Create Client & Continue'} <FaArrowRight style={{ marginLeft: '0.5rem' }} />
                                </>
                            ) : (
                                <>
                                    Next <FaArrowRight style={{ marginLeft: '0.5rem' }} />
                                </>
                            )}
                        </button>
                    ) : (
                        <button 
                            className={styles.submitButton}
                            onClick={handleComplete}
                            disabled={loading || onboardingLoading || isValidating || isCheckingSimilarity || Object.keys(errors).length > 0 || !onboardingEmail || emailError}
                        >
                            <FaCheckCircle style={{ marginRight: '0.5rem' }} />
                            {loading || onboardingLoading ? 'Starting Onboarding...' : 'Begin Onboarding'}
                        </button>
                    )}
                </div>
            </div>

            {/* Confirmation Dialog for Similar Clients */}
            {showConfirmDialog && (() => {
                const exactMatches = similarClients.filter(client => 
                    client.clientName && 
                    client.clientName.toLowerCase() === formData.clientName.trim().toLowerCase()
                );
                const similarMatches = similarClients.filter(client => 
                    client.clientName && 
                    client.clientName.toLowerCase() !== formData.clientName.trim().toLowerCase()
                );
                
                return (
                    <div className={styles.modal}>
                        <div className={styles.modalContent}>
                            <h3>{exactMatches.length > 0 ? '🚨 Duplicate Client Warning' : '⚠️ Potential Duplicate Client'}</h3>
                            
                            {exactMatches.length > 0 && (
                                <>
                                    <p><strong>An exact match was found:</strong></p>
                                    <div className={styles.similarClientsList}>
                                        <ul>
                                            {exactMatches.map(client => (
                                                <li key={client.clientId} style={{ color: '#d32f2f', fontWeight: 'bold' }}>
                                                    {client.clientCode ? `${client.clientCode} - ` : ''}{client.clientName}
                                                    {!client.isActive && (
                                                        <span className={styles.inactiveClient}> (Inactive)</span>
                                                    )}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </>
                            )}
                            
                            {similarMatches.length > 0 && (
                                <>
                                    {exactMatches.length > 0 && <br />}
                                    <p><strong>Similar clients were also found:</strong></p>
                                    <div className={styles.similarClientsList}>
                                        <ul>
                                            {similarMatches.map(client => (
                                                <li key={client.clientId}>
                                                    <strong>
                                                        {client.clientCode ? `${client.clientCode} - ` : ''}{client.clientName}
                                                    </strong>
                                                    {!client.isActive && (
                                                        <span className={styles.inactiveClient}> (Inactive)</span>
                                                    )}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </>
                            )}
                            
                            <p>
                                {exactMatches.length > 0 
                                    ? 'This appears to be a duplicate. Are you sure you want to onboard this client anyway?'
                                    : 'Are you sure you want to onboard this new client?'
                                }
                            </p>
                            <div className={styles.formActions}>
                                <button 
                                    type="button" 
                                    className={styles.cancelButton}
                                    onClick={() => setShowConfirmDialog(false)}
                                    disabled={loading}
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="button" 
                                    className={styles.submitButton}
                                    onClick={createClientAndContinue}
                                    disabled={loading}
                                >
                                    {loading ? 'Creating...' : 'Yes, Create and Continue'}
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}
        </div>
    );
};

export default OnboardClient;
